import type { InfiniteData, QueryClient } from '@tanstack/react-query'
import { authed } from './auth'
import { channelMuted } from './muting'
import { compareIds } from './messageMerge'
import { ChannelType, type AttachmentRef, type Channel, type ForumTag, type Message, type ReadState, type UserGuildSettings } from '@/types/api'

export interface ForumPost { thread: Channel; message?: Message | undefined; reply_count: number }
export interface ForumPage { posts: ForumPost[]; has_more: boolean; next_before: string | null }
export interface ForumFilter { query: string; sort: 'activity' | 'created'; archived: boolean; tags?: string[] }
export const forumKey = (userId: string, channelId: string) => ['forum-posts', userId, channelId] as const
export const forumApi = {
  list: (channelId: string, filter: ForumFilter, before?: string, signal?: AbortSignal) => {
    const params = new URLSearchParams({ query: filter.query, sort: filter.sort, archived: String(filter.archived), limit: '30' })
    if (filter.tags?.length) params.set('tags', [...filter.tags].sort().join(','))
    if (before) params.set('before', before)
    return authed<ForumPage>(`/channels/${channelId}/forum-posts?${params}`, signal ? { signal } : undefined)
  },
  create: (channelId: string, name: string, content: string, options?: { attachments?: AttachmentRef[]; applied_tags?: string[] }) => authed<ForumPost>(`/channels/${channelId}/forum-posts`, { method: 'POST', body: JSON.stringify({ name: name.trim(), content: content.trim(), ...options }) }),
  setTags: (channelId: string, tags: { id?: string; name: string }[]) => authed<Channel>(`/channels/${channelId}/forum-tags`, { method: 'PUT', body: JSON.stringify({ available_tags: tags }) }),
  update: (channelId: string, threadId: string, data: { applied_tags?: string[]; forum_pinned?: boolean }) => authed<Channel>(`/channels/${channelId}/forum-posts/${threadId}`, { method: 'PATCH', body: JSON.stringify(data) }),
}

export const MAX_FORUM_TAGS = 20
export const MAX_TOPIC_TAGS = 5
export function selectedForumTags(tags: ForumTag[], ids: string[]): string[] { const allowed = new Set(tags.map(tag => tag.id)); return [...new Set(ids)].filter(id => allowed.has(id)).slice(0, MAX_TOPIC_TAGS) }

export function forumPreview(post: ForumPost, blocked = false): string {
  if (blocked) return 'Сообщение заблокированного пользователя'
  if (!post.message) return 'Первое сообщение удалено'
  return post.message.content || post.message.attachments?.[0]?.filename || 'Сообщение без текста'
}

/** Card media never reveals a spoiler or a blocked participant's attachment. */
export function forumThumbnail(post: ForumPost, blocked = false): string | undefined {
  if (blocked) return undefined
  const image = post.message?.attachments?.find(a => a.content_type.startsWith('image/') && !a.blocked && !a.spoiler && !a.filename.startsWith('SPOILER_'))
  const src = image?.proxy_url || image?.url
  if (!src) return undefined
  try { const url = new URL(src, 'https://klichat.ru'); return ['https:', 'http:'].includes(url.protocol) ? src : undefined } catch { return undefined }
}

/** Message IDs share the Discord epoch; a reply changes activity without changing the starter. */
export function forumActivityDate(thread: Channel, starter?: Message): Date | undefined {
  if (thread.last_message_id && /^\d+$/.test(thread.last_message_id)) {
    const date = new Date(Number(BigInt(thread.last_message_id) >> 22n) + 1420070400000)
    if (!Number.isNaN(date.getTime())) return date
  }
  const date = new Date(thread.thread_metadata?.create_timestamp ?? starter?.timestamp ?? '')
  return Number.isNaN(date.getTime()) ? undefined : date
}

/** Старые запросы отменяются до изменения карточек: удалённый текст не возвращается. */
export function applyForumEvent(client: QueryClient, userId: string | undefined, type: string, data: unknown, channels?: Record<string, Record<string, Channel>>) {
  if (!userId) return
  if (type === 'READY') { void client.invalidateQueries({ queryKey: ['forum-posts', userId] }); return }
  if (!['MESSAGE_REACTION_ADD','MESSAGE_REACTION_REMOVE','MESSAGE_REACTION_REMOVE_ALL','MESSAGE_REACTION_REMOVE_EMOJI','MESSAGE_CREATE','MESSAGE_UPDATE','MESSAGE_DELETE','MESSAGE_DELETE_BULK','THREAD_CREATE','THREAD_UPDATE','THREAD_MEMBERS_UPDATE','THREAD_DELETE','CHANNEL_UPDATE','CHANNEL_DELETE','GUILD_MEMBER_UPDATE','GUILD_ROLE_UPDATE','GUILD_ROLE_DELETE'].includes(type)) return
  const event = data as Partial<Message & Channel> & { ids?: string[]; added_members?: NonNullable<Channel['member']>[]; removed_member_ids?: string[] }
  const cached = client.getQueriesData<InfiniteData<ForumPage>>({ queryKey: ['forum-posts', userId] })
  const parent = event.parent_id ?? (event.guild_id && event.channel_id ? channels?.[event.guild_id]?.[event.channel_id]?.parent_id : undefined)
  for (const [key, pages] of cached) {
    const affected = key[2] === parent || key[2] === event.id || pages?.pages.some(page => page.posts.some(post => post.thread.id === event.channel_id || post.thread.id === event.id)) || type.startsWith('GUILD_') || (!parent && type.startsWith('MESSAGE_DELETE'))
    if (!affected) continue
    void client.cancelQueries({ queryKey: key, exact: true })
    const deleted = new Set(type === 'MESSAGE_DELETE_BULK' ? event.ids : type === 'MESSAGE_DELETE' ? [event.id] : [])
    if (pages) client.setQueryData<InfiniteData<ForumPage>>(key, { ...pages, pages: pages.pages.map(page => ({ ...page, posts: page.posts.filter(post => type !== 'THREAD_DELETE' || post.thread.id !== event.id).map(post => {
      if (post.thread.id === event.channel_id && post.message && deleted.has(post.message.id)) return { ...post, message: undefined }
      if (type === 'MESSAGE_UPDATE' && post.thread.id === event.channel_id && post.message?.id === event.id) return { ...post, message: { ...post.message, ...event } as Message }
      if (type === 'THREAD_UPDATE' && post.thread.id === event.id) return { ...post, thread: { ...post.thread, ...event } as Channel }
      if (type === 'THREAD_MEMBERS_UPDATE' && post.thread.id === event.id) {
        const added = event.added_members?.find(member => member.user_id === userId)
        const removed = event.removed_member_ids?.includes(userId)
        return { ...post, thread: { ...post.thread, ...(added ? { member: added } : removed ? { member: null } : {}) } }
      }
      return post
    }) })) })
    void client.invalidateQueries({ queryKey: key, exact: true })
  }
}

/** Новые темы и непрочитанные ответы в подписках зажигают индикатор форума. */
export function forumActivity(forum: Channel, channels: Record<string, Channel>, readStates: Record<string, ReadState>, settings: UserGuildSettings | undefined): { unread: boolean; mentions: number } {
  let unread = false
  let mentions = 0
  for (const channel of Object.values(channels)) {
    if (channel.id !== forum.id && !(channel.type === ChannelType.PublicThread && channel.parent_id === forum.id)) continue
    const state = readStates[channel.id]
    const count = state?.mention_count ?? 0
    if (channel.id !== forum.id && !channel.member && !count) continue
    if (channelMuted(settings, channel.id, channels)) continue
    if (channel.last_message_id && (!state?.last_message_id || compareIds(channel.last_message_id, state.last_message_id) > 0) || count > 0) unread = true
    mentions += count
  }
  return { unread, mentions }
}

/** Публичные события не содержат собственного участия; список возвращает его отдельно. */
export function forumThreadState(cached: Channel, live: Channel | undefined): Channel {
  return { ...cached, ...live, member: live && live.member !== undefined ? live.member : cached.member ?? null }
}
